import React, { useMemo, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import {
  addMinutesToClock,
  ensureDurationOption,
  ensureTimeOption,
  formatDurationLabel,
} from '../../utils/bookingSchedule';
import { StoredQuote } from '../../utils/quoteRecord';
import { PlaceSearch, ShootMap } from '../Common/ShootLocationField';

interface ReceiptRow {
  amount: string;
  paidAt: string;
  note: string;
}

interface RecordExistingBookingProps {
  onCancel: () => void;
  onSaved: (quote: StoredQuote) => void;
}

const emptyReceipt = (): ReceiptRow => ({ amount: '', paidAt: '', note: '' });

const RecordExistingBooking: React.FC<RecordExistingBookingProps> = ({ onCancel, onSaved }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+27');
  const [preference, setPreference] = useState<'either' | 'email' | 'whatsapp'>('either');
  const [company, setCompany] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [description, setDescription] = useState('');
  const [shootType, setShootType] = useState('');
  const [total, setTotal] = useState('');
  const [depositPercent, setDepositPercent] = useState('50');
  const [externalReference, setExternalReference] = useState('');
  const [receipts, setReceipts] = useState<ReceiptRow[]>([emptyReceipt()]);
  const [shootDate, setShootDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [locationName, setLocationName] = useState('');
  const [address, setAddress] = useState('');
  const [mapsUrl, setMapsUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const endTime = useMemo(() => addMinutesToClock(startTime, durationMinutes), [startTime, durationMinutes]);
  const timeOptions = ensureTimeOption(startTime);
  const durationOptions = ensureDurationOption(durationMinutes);

  const updateReceipt = (index: number, patch: Partial<ReceiptRow>) => {
    setReceipts((rows) => rows.map((row, item) => (item === index ? { ...row, ...patch } : row)));
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      const filledReceipts = receipts
        .filter((row) => String(row.amount || '').trim())
        .map((row) => ({
          amount: row.amount.trim(),
          paidAt: row.paidAt || undefined,
          note: row.note.trim(),
        }));
      const booking = (shootDate || startTime || locationName || address || mapsUrl.trim())
        ? {
          shootDate,
          startTime,
          endTime: startTime ? endTime : '',
          locationName,
          address,
          mapsUrl: mapsUrl.trim(),
          dateConfirmed: Boolean(shootDate && startTime),
        }
        : undefined;
      const response = await fetch(`${API_BASE_URL}/quotes/recorded`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client: {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim(),
            countryCode,
            preference,
            company: company.trim(),
            billingAddress: billingAddress.trim(),
          },
          description: description.trim(),
          shootType: shootType.trim(),
          total: total.trim(),
          depositPercent: Number(depositPercent) || 50,
          externalReference: externalReference.trim(),
          receipts: filledReceipts,
          booking,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(body.error || 'The existing booking could not be recorded.');
        return;
      }
      onSaved(body.quote as StoredQuote);
    } catch (_) {
      setError('The existing booking could not be recorded.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="quote-schedule record-existing-booking" onSubmit={save}>
      <h3>Record existing booking</h3>
      <p>Enter the client, what was quoted, and any money already received. Nothing is emailed.</p>
      {error ? <p className="quote-history-note">{error}</p> : null}

      <h4>Client</h4>
      <label>Name<input required value={name} onChange={(event) => setName(event.target.value)} /></label>
      <label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
      <label>Country code<input value={countryCode} onChange={(event) => setCountryCode(event.target.value)} /></label>
      <label>Mobile<input value={phone} onChange={(event) => setPhone(event.target.value)} /></label>
      <label>Preference
        <select value={preference} onChange={(event) => setPreference(event.target.value as 'either' | 'email' | 'whatsapp')}>
          <option value="either">Email or WhatsApp</option>
          <option value="email">Email</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
      </label>
      <label>Company<input value={company} onChange={(event) => setCompany(event.target.value)} /></label>
      <label>
        Billing address (optional)
        <textarea aria-label="Billing address" value={billingAddress} rows={4} maxLength={500} onChange={(event) => setBillingAddress(event.target.value)} placeholder={'12 Loop Street\nGardens\nCape Town\n8001'} />
      </label>

      <h4>Quotation</h4>
      <label>What was quoted<input required value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Essence Portrait" /></label>
      <label>Shoot type<input value={shootType} onChange={(event) => setShootType(event.target.value)} /></label>
      <label>Total (rand)<input required inputMode="decimal" value={total} onChange={(event) => setTotal(event.target.value)} placeholder="8500" /></label>
      <label>Deposit percent<input inputMode="decimal" value={depositPercent} onChange={(event) => setDepositPercent(event.target.value)} /></label>
      <label>Previous reference<input value={externalReference} onChange={(event) => setExternalReference(event.target.value)} placeholder="Golden quote number" /></label>

      <h4>Money already received</h4>
      {receipts.map((row, index) => (
        <div className="record-receipt-row" key={`receipt-${index}`}>
          <label>Amount (rand)<input inputMode="decimal" value={row.amount} onChange={(event) => updateReceipt(index, { amount: event.target.value })} /></label>
          <label>Date paid<input type="date" value={row.paidAt} onChange={(event) => updateReceipt(index, { paidAt: event.target.value })} /></label>
          <label>Note<input value={row.note} onChange={(event) => updateReceipt(index, { note: event.target.value })} placeholder="EFT, cash, reference" /></label>
        </div>
      ))}
      <button type="button" className="btn-secondary btn-sm" onClick={() => setReceipts((rows) => [...rows, emptyReceipt()])}>Add another payment</button>

      <h4>Shoot (optional)</h4>
      <label>Shoot date<input type="date" value={shootDate} onChange={(event) => setShootDate(event.target.value)} /></label>
      <label>Start time
        <select value={startTime} onChange={(event) => setStartTime(event.target.value)}>
          <option value="">Start time</option>
          {timeOptions.map((time) => <option key={time} value={time}>{time}</option>)}
        </select>
      </label>
      <label>Duration
        <select value={String(durationMinutes)} onChange={(event) => setDurationMinutes(Number(event.target.value))}>
          {durationOptions.map((minutes) => <option key={minutes} value={minutes}>{formatDurationLabel(minutes)}</option>)}
        </select>
      </label>
      {startTime ? <p className="quote-history-note">Ends {endTime}</p> : null}
      <PlaceSearch onSelect={(place) => { setLocationName(place.name); setAddress(place.address); setMapsUrl(place.mapsUrl); }} />
      <ShootMap mapsUrl={mapsUrl} label={locationName || address} />
      <label>Location<input value={locationName} onChange={(event) => setLocationName(event.target.value)} /></label>
      <label>Address<input value={address} onChange={(event) => setAddress(event.target.value)} /></label>
      <label>Google Maps link<input type="url" value={mapsUrl} onChange={(event) => setMapsUrl(event.target.value)} placeholder="https://maps.app.goo.gl/…" /></label>

      <div className="quote-action-row">
        <button type="submit" className="btn-primary btn-sm" disabled={saving}>{saving ? 'Saving…' : 'Save booking'}</button>
        <button type="button" className="btn-secondary btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
};

export default RecordExistingBooking;
